import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryGridSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryGo = (url: string) => {
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <section id="shop-categories" className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-4">
            Shop by Category
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
            Explore Our Product Categories
          </h2>
          <p className="mt-4 text-foreground-600 text-base md:text-lg max-w-xl mx-auto">
            Browse through curated collections and find exactly what you need. Select a category or use the dropdown below.
          </p>

          <div className="mt-8 flex justify-center" ref={dropdownRef}>
            <div className="relative w-full max-w-md">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-200 rounded-xl text-foreground-800 text-sm font-medium hover:border-primary-300 transition-all duration-200 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-primary-500"></i>
                  Select a category to browse...
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-foreground-500 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 z-30 max-h-72 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryGo(cat.url)}
                      className="w-full text-left px-4 py-3 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 flex items-center gap-3 cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-6 h-6 flex items-center justify-center text-primary-500">
                        <i className={cat.icon}></i>
                      </span>
                      <span>{cat.name}</span>
                      <i className="ri-external-link-line ml-auto text-foreground-400 text-xs"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer overflow-hidden"
            >
              <div className="w-full aspect-square overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex flex-col items-center gap-1.5 p-3 md:p-4">
                <span className="text-xs md:text-sm font-semibold text-foreground-800 group-hover:text-primary-700 text-center leading-tight transition-colors duration-300 whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="text-[10px] md:text-xs text-foreground-500 group-hover:text-primary-500 text-center leading-tight hidden md:block">
                  {cat.description}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}